/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

/**
 * Internal dependencies
 */
import CacheList from '@Components/AdminCache/CacheList';
import CacheStatsCard from '@Components/AdminCache/CacheStatsCard';
import Breadcrumbs from '@Components/Breadcrumbs';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import Heading from '@Components/Heading';
import { useCacheStats, useDeleteCacheEntries } from '@Hooks/useCache';

export default function AdminCache() {
  const [flushAllOpen, setFlushAllOpen] = useState(false);
  const { data: stats } = useCacheStats();
  const deleteMutation = useDeleteCacheEntries();

  const handleFlushAll = async () => {
    try {
      const result = await deleteMutation.mutateAsync({ all: true });
      toastSuccess(`Flushed ${result.removed} cache entries.`);
      setFlushAllOpen(false);
    } catch (err) {
      toastError(err instanceof Error ? err.message : 'Failed to flush cache');
    }
  };

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/admin/cache' },
          { name: 'Cache', href: '/admin/cache' },
        ]}
      />

      <div className="mt-8 mb-4 flex items-center justify-between">
        <div>
          <Heading>Cache Management</Heading>
          <p className="text-sm text-zinc-500">
            Inspect and flush entries in the shared app cache.
          </p>
        </div>
      </div>

      <div className="mb-6">
        <CacheStatsCard />
      </div>

      <CacheList
        renderHeaderActions={() => (
          <Button
            variant="destructive"
            onClick={() => setFlushAllOpen(true)}
            disabled={!stats?.count}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Flush entire cache
          </Button>
        )}
      />

      <ConfirmationDialog
        open={flushAllOpen}
        onOpenChange={setFlushAllOpen}
        onSubmit={handleFlushAll}
        match="FLUSH ALL"
        title="Flush entire cache?"
        description="This will remove every entry across all tenants, restaurants, and resources."
        matchLabel='Type "FLUSH ALL" to confirm'
        submitLabel="Flush all"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
